import { datadogRum } from "@datadog/browser-rum";
// eslint-disable-next-line no-restricted-imports
import { segmentPluginHelper } from "@src/lib/analytics/src/lib/segmentPluginHelper";
import { type JsonValue } from "type-fest";

import { getIsEmbedded } from "../../EmbeddedMode/getIsEmbedded";
import { isCapacitorPlatform } from "../utils/capacitor/platformUtils";
import { serializeError, toJsonRecord } from "./utils";

export interface LogErrorProps {
  // try catch errors are `unknown` type.
  error: unknown;
  metadata?: Record<string, unknown>;
}

export type EventLogger = (eventKey: string, properties?: Record<string, unknown>) => void;
export type ErrorLogger = (eventName: string, params: LogErrorProps) => void;

const trackSegmentEvent = ({
  eventKey,
  properties,
}: {
  eventKey: string;
  properties?: Record<string, JsonValue>;
}) => {
  if (isCapacitorPlatform()) {
    void segmentPluginHelper.capacitorTrack(eventKey, properties);
  } else {
    segmentPluginHelper.webTrack(eventKey, properties);
  }
};

export function logEvent(
  eventKey: string,
  properties?: Record<string, unknown>,
  trackSegment = true
): void {
  const isEmbedded = getIsEmbedded();
  const safeProperties = properties ? toJsonRecord(properties) : undefined;
  const propertiesWithEmbedded = { ...safeProperties, isEmbedded };

  datadogRum.addAction(eventKey, propertiesWithEmbedded);

  if (trackSegment) {
    trackSegmentEvent({ eventKey, properties: propertiesWithEmbedded });
  }
}

export function logError(eventName: string, params: LogErrorProps, trackSegment = false): void {
  const { error: unknownError, metadata } = params;
  const isError = unknownError instanceof Error;
  const error = isError ? unknownError : String(unknownError);
  const safeMetadata = metadata ? toJsonRecord(metadata) : {};
  const serializedError = serializeError(unknownError);

  datadogRum.addError(error, {
    eventName,
    ...safeMetadata,
  });

  if (trackSegment) {
    trackSegmentEvent({
      eventKey: eventName,
      properties: {
        ...serializedError,
        ...safeMetadata,
      },
    });
  }
}

interface DeprecatedDoNotUseLogErrorProps {
  message: string;
  metadata?: Record<string, unknown>;
}

/**
 * @deprecated
 * Use logError from @src/appV2/lib/analytics
 * FIXME: Migrate all usages of deprecatedDoNotUseLogError in src/app to directly use logError
 */
export function deprecatedDoNotUseLogError(params: DeprecatedDoNotUseLogErrorProps): void {
  const { message, metadata } = params;
  logError("Legacy Error Logs from src/app", { error: message, metadata });
}
